import { useEffect, useRef, useState } from "react";
import { ArrowRight, PlayCircle } from "lucide-react";
import EngineHub from "./EngineHub";
import HeroBackground from "./HeroBackground";

const TRUST_POINTS = [
  "No local setup",
  "Browser-based ranges",
  "Framework-aligned",
];

function OrbitTrack({ children, delay = "0s" }) {
  const ref = useRef(null);
  const [box, setBox] = useState(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width && height) setBox({ w: width, h: height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // rounded-lg is 8px; the track sits 5px out from the button
  const PAD = 5;
  const R = 13;
  const w = box ? box.w + PAD * 2 : 0;
  const h = box ? box.h + PAD * 2 : 0;
  // a rounded rect traced clockwise from the top-left arc
  const path = box
    ? `M${R},1 H${w - R} A${R - 1},${R - 1} 0 0 1 ${w - 1},${R} V${h - R} A${R - 1},${R - 1} 0 0 1 ${w - R},${h - 1} H${R} A${R - 1},${R - 1} 0 0 1 1,${h - R} V${R} A${R - 1},${R - 1} 0 0 1 ${R},1 Z`
    : "";

  return (
    <span ref={ref} className="relative inline-flex">
      {box && (
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute"
          style={{
            left: -PAD,
            top: -PAD,
            width: w,
            height: h,
            overflow: "visible",
          }}
        >
          {/* the track itself stays invisible — only the packet reads */}
          <circle
            r="2.6"
            fill="var(--color-primary)"
            style={{
              filter: "drop-shadow(0 0 5px var(--color-shadow-primary))",
            }}
          >
            <animateMotion
              dur="4s"
              repeatCount="indefinite"
              begin={delay}
              path={path}
            />
          </circle>
        </svg>
      )}
      {children}
    </span>
  );
}

export default function Hero({ onLogin, onSignup }) {
  return (
    <section className="relative flex min-h-screen items-center overflow-hidden pt-36 pb-20 lg:pt-44 lg:pb-28">
      <HeroBackground />

      <div className="relative z-10 mx-auto grid w-full max-w-350 grid-cols-1 items-center gap-14 px-5 md:px-10 lg:grid-cols-2">
        {/* Copy */}
        <div className="max-w-xl">
          <div
            className="lp-rise inline-flex items-center gap-2 rounded-full border border-(--color-border-solid) bg-(--color-bg-card) px-3.5 py-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-(--color-text-secondary)"
            style={{ "--lp-delay": "0ms" }}
          >
            <span className="cyber-badge-dot" />
            All-in-One Cyber Range Platform
          </div>

          <h1
            className="lp-rise qv-glitch mt-6 text-[32px] sm:text-[40px] leading-tight font-normal tracking-tight text-(--color-text-primary)"
            style={{ "--lp-delay": "90ms" }}
          >
            Train, attack, and defend on a{" "}
            <span className="lp-highlight">live cyber range</span>
          </h1>

          <p
            className="lp-rise qv-mono mt-6 max-w-lg border-l-2 pl-4 text-[13.5px] sm:text-sm leading-relaxed"
            style={{
              "--lp-delay": "180ms",
              borderColor:
                "color-mix(in srgb, var(--color-primary) 40%, transparent)",
            }}
          >
            <span className="font-medium" style={{ color: "#c084fc" }}>
              const
            </span>{" "}
            <span style={{ color: "#2dd4bf" }}>mission</span>{" "}
            <span className="text-(--color-text-muted)">=</span>{" "}
            <span
              style={{
                color:
                  "color-mix(in srgb, var(--color-primary) 38%, var(--color-text-secondary))",
              }}
            >
              "Spin up on-demand attack boxes, secure VPN access, and
              AI-designed network topologies — so teams can practise real
              offensive and defensive scenarios without touching local
              infrastructure."
            </span>
            <span className="text-(--color-text-muted)">;</span>
          </p>

          <div
            className="lp-rise mt-9 flex flex-wrap items-center gap-4"
            style={{ "--lp-delay": "270ms" }}
          >
            {/* a packet orbits each CTA on a faint track — the same motif as the
                stats orbs, the engine spokes and the range wire */}
            <OrbitTrack>
              <button
                type="button"
                onClick={onSignup}
                className="group relative inline-flex items-center gap-2 rounded-lg bg-[linear-gradient(180deg,var(--color-primary),var(--color-primary-hover))] px-6 py-3.5 text-sm font-semibold text-(--color-text-on-primary) shadow-[0_0_18px_var(--color-shadow-primary)] transition-all duration-300 hover:-translate-y-px hover:shadow-[0_0_30px_var(--color-shadow-primary-hover)]"
              >
                Get Started Free
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </OrbitTrack>
            <OrbitTrack delay="2s">
              {/* same auth console the nav opens — signing in never leaves
                  the page */}
              <button
                type="button"
                onClick={onLogin}
                className="group relative inline-flex items-center gap-2 rounded-lg border border-(--color-border-solid) px-6 py-3.5 text-sm font-medium text-(--color-text-primary) transition-all duration-300 hover:border-(--color-primary) hover:text-(--color-primary)"
              >
                <PlayCircle className="h-4 w-4" />
                Sign In
              </button>
            </OrbitTrack>
          </div>

          <div
            className="lp-rise mt-9 flex flex-wrap items-center gap-x-6 gap-y-2"
            style={{ "--lp-delay": "360ms" }}
          >
            {TRUST_POINTS.map((point) => (
              <span
                key={point}
                className="flex items-center gap-2 text-[12.5px] text-(--color-text-muted)"
              >
                <span className="h-1 w-1 rounded-full bg-(--color-primary)" />
                {point}
              </span>
            ))}
          </div>
        </div>

        {/* Visual */}
        <div className="lp-rise" style={{ "--lp-delay": "220ms" }}>
          <EngineHub />
        </div>
      </div>
    </section>
  );
}
